import { useSyncExternalStore } from 'react'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * Light / Dark / System, as on the FursaHub website's web app
 * (Website/fursahub/components/WebApp/Theme). "system" follows the device.
 * Saved per browser. index.html applies the saved choice before the first
 * paint, so a dark page never flashes white on load.
 */
export type ThemePreference = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

export const THEME_PREFERENCES: ThemePreference[] = ['light', 'dark', 'system']

/** Must match the key the inline script in index.html reads. */
export const THEME_STORAGE_KEY = 'fursahub-admin-theme'

interface ThemeState {
  preference: ThemePreference
  setPreference: (preference: ThemePreference) => void
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      preference: 'light',
      setPreference: (preference) => set({ preference }),
    }),
    { name: THEME_STORAGE_KEY },
  ),
)

const DARK_QUERY = '(prefers-color-scheme: dark)'

function subscribeToDevice(onChange: () => void): () => void {
  const query = window.matchMedia(DARK_QUERY)
  query.addEventListener('change', onChange)
  return () => query.removeEventListener('change', onChange)
}

/** The theme actually showing: the choice, or the device's when it's "system". */
export function useResolvedTheme(): ResolvedTheme {
  const preference = useThemeStore((s) => s.preference)
  const deviceDark = useSyncExternalStore(
    subscribeToDevice,
    () => window.matchMedia(DARK_QUERY).matches,
    () => false,
  )
  if (preference === 'system') return deviceDark ? 'dark' : 'light'
  return preference
}
